import React, { FC } from "react";

import { Badge, InfoMessage } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import ProjectsService from "@/services/ProjectsService";

import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

import { Dot } from "@/components/atoms/Dot/Dot";
import { MembersCard } from "@/components/organisms/MembersCard/MembersCard";
import { PageLayout } from "@/components/templates";

import { getApiErrormessage } from "@/utils/UAPIErrors";
import { isSup } from "@/utils/URoles";
import { workspaceLabel } from "@/utils/UWorkspaces";

import { projectKeys } from "@/constants/CQueryKeys";
import { ResourceRole } from "@/constants/CRoles";

import { ProjectBreadcrumb } from "./components/ProjectBreadcrumb";
import { ProjectOverviewTab } from "./components/ProjectOverviewTab";
import { ProjectSettingsTab } from "./components/ProjectSettingsTab";

const TABS = ["overview", "members", "settings"] as const;
type TTab = (typeof TABS)[number];

const isTab = (value: string | null): value is TTab =>
	TABS.includes(value as TTab);

/**
 * `/projects/:id` - one project under its client: breadcrumb with a
 * sibling select, then the "Aperçu", "Membres" and "Réglages" tabs
 * (kept in `?tab=`). Tasks and reports stay where they are today (the
 * home page, /reports).
 */
export const ProjectDetailView: FC = () => {
	const { id: idParam } = useParams<{ id: string }>();
	const id = Number(idParam);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const tabParam = searchParams.get("tab");
	const sharingOn = useFeatureFlag("sharing");
	// Members belong to sharing: paused, the tab goes, and so does its URL.
	const tab: TTab =
		isTab(tabParam) && (tabParam !== "members" || sharingOn)
			? tabParam
			: "overview";

	const {
		data: project,
		isLoading,
		error,
	} = useQuery({
		queryKey: projectKeys.detail(id),
		queryFn: () => ProjectsService.getProject(id),
		enabled: !Number.isNaN(id),
	});

	const { user } = useAuth();
	const role = project?.role;
	const canEdit = isSup(ResourceRole.MAINTAINER, role);
	const canAdmin = isSup(ResourceRole.ADMIN, role);

	return (
		<PageLayout
			title={
				project ? (
					<span className="flex items-center gap-3">
						<Dot
							color={project.color}
							className="h-4 w-4 min-w-4 rounded-md"
						/>
						{project.name}
					</span>
				) : (
					t("projects.label")
				)
			}
			titleAfter={
				project?.isArchived ? (
					<Badge
						size="sm"
						variant="tint"
						color="neutral"
						picto="archive"
					>
						{t("projects.infos.archived")}
					</Badge>
				) : undefined
			}
			isLoading={isLoading}
			error={
				error
					? getApiErrormessage(error)
					: !project
						? t("projects.notFound")
						: false
			}
			onBack={() =>
				navigate(
					project?.client?.canOpen
						? `/clients/${project.client.id}`
						: "/projects"
				)
			}
			onBackLabel={t("projects.back")}
			tabs={[
				{ value: "overview", label: t("projects.tabs.overview") },
				...(sharingOn
					? [{ value: "members", label: t("members.title") }]
					: []),
				{ value: "settings", label: t("projects.tabs.settings") },
			]}
			selectedTabId={tab}
			onSelectTab={(value) => {
				const next = new URLSearchParams(searchParams);
				if (value === "overview") next.delete("tab");
				else next.set("tab", value);
				setSearchParams(next, { replace: true });
			}}
		>
			{!!project && (
				<div className="flex flex-col gap-6">
					<ProjectBreadcrumb project={project} />
					{/* Someone else's workspace: say so, once, in words. */}
					{project.workspace && !project.workspace.mine && (
						<InfoMessage data-testid="shared-project-banner">
							{t("projects.sharedBanner", {
								workspace: workspaceLabel(project.workspace),
								role: t(`roles.${project.role}`),
							})}
						</InfoMessage>
					)}
					{project.description && (
						<p className="-mt-3 text-sm text-neutral-500">
							{project.description}
						</p>
					)}
					{tab === "overview" && (
						<ProjectOverviewTab project={project} />
					)}
					{tab === "members" && (
						<MembersCard
							scope="projects"
							id={project.id}
							canManage={canAdmin}
							currentUserId={user?.id ?? 0}
							className="max-w-3xl"
						/>
					)}
					{tab === "settings" && (
						<ProjectSettingsTab
							project={project}
							canEdit={canEdit}
							canAdmin={canAdmin}
						/>
					)}
				</div>
			)}
		</PageLayout>
	);
};
